import React, { Component, Fragment } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import RejectActionConfirmation from "./RejectActionConfirmation";
import Button from "../../../shared/button/Button";
import Sending from "../../../shared/loaders/Sending";
import ModalThankYou from "../../../shared/loaders/ModalThankYou";
import ApplicationRejectionReason from "../../../../apiResources/ApplicationRejectionReason";

import {
  Wrapper,
  Form,
  Label,
  Field,
  TextInput,
  ButtonsWrapper,
  Error,
  Note,
  PlaceholderBtn,
  Asterisk,
  Select,
  CheckBoxInput,
  CheckBoxLabel,
  CheckBoxesGroup,
  CheckBoxesGroupItem,
  Indicator,
  Required,
  RejectionReasonLabel,
} from "./styles/reject-candidate-styles";

class RejectCandidate extends Component {
  constructor(props) {
    super(props);
    const { cardData } = this.props;
    const { status } = cardData;

    this.state = {
      formLoading: false,
      successMessageActive: false,
      rejectMessage: "",
      actionConfirmationActive: status === "offered",
      error: null,
      selectedReason: "", // Primary reason
      selectedSubReasons: [], // multiple sub reasons
      rejectionReasons: [], // start empty
    };
  }

  componentDidMount() {
    ApplicationRejectionReason.all()
      .then((reasons) => {
        this.setState({ rejectionReasons: reasons.data });
      })
      .catch((err) => {
        this.setState({
          error: { message: "Failed to load rejection reasons" },
        });
      });
  }

  showSuccessMessage = () => {
    this.setState({ successMessageActive: true });
  };

  handleSubmit = (e) => {
    e.preventDefault();

    // Validate primary reason is selected
    if (!this.state.selectedReason) {
      this.setState({ error: { message: "Please select a primary reason" } });
      return;
    }

    const selectedReasonData = this.state.rejectionReasons.find(
      (r) => String(r.id) === this.state.selectedReason
    );
    const isOtherReason = selectedReasonData?.name?.toLowerCase() === "other";
    if (isOtherReason && !this.state.rejectMessage.trim()) {
      this.setState({
        error: { message: "Please provide feedback when selecting Other" },
      });
      return;
    }

    this.setState({ formLoading: true, error: null });
    const { rejectMessage } = this.state;
    const { cardData, closeActionModalAfterSubmit, fetchKanbanData } =
      this.props;
    const { updateStatusLink } = cardData;
    const url = updateStatusLink;

    const formData = new FormData();

    formData.append("application_action[action]", "rejected_candidate");
    formData.append("application_action[message]", rejectMessage);
    formData.append("application_action[action_at]", Date.now());
    formData.append(
      "application_action[application_action_rejection_reason_attributes][primary_reason_id]",
      this.state.selectedReason
    );

    this.state.selectedSubReasons.forEach((subReasonId) => {
      formData.append(
        `application_action[application_action_rejection_reason_attributes][sub_reason_ids][]`,
        subReasonId
      );
    });

    const payload = {
      url,
      method: "post",
      headers: { "Content-Type": "multipart/form-data" },
      data: formData,
    };

    axios(payload)
      .then(() => {
        this.setState({ formLoading: false, error: null });
        this.showSuccessMessage();
        setTimeout(() => {
          closeActionModalAfterSubmit();
          fetchKanbanData({ loading: false, updating: true });
        }, 2000);
      })
      .catch((error) => {
        this.setState({
          error: error.response?.data?.errors || null,
          formLoading: false,
        });
      });
  };

  handleRejectMessage = (e) => {
    this.setState({ rejectMessage: e.target.value, error: null });
  };

  handleCancel = (e) => {
    e.preventDefault();
    const { closeActionModalAfterReset } = this.props;
    this.setState({ rejectMessage: "", error: null });
    closeActionModalAfterReset();
  };

  handleActionConfirmation = (e) => {
    e.preventDefault();
    this.setState({ actionConfirmationActive: false });
  };

  handleReasonChange = (e) => {
    this.setState({ selectedReason: e.target.value, selectedSubReasons: [] });
  };

  handleSubReasonChange = (e) => {
    const { value, checked } = e.target;
    const intValue = parseInt(value, 10);

    this.setState((prevState) => ({
      selectedSubReasons: checked
        ? [...prevState.selectedSubReasons, intValue]
        : prevState.selectedSubReasons.filter((r) => r !== intValue),
    }));
  };

  render() {
    const {
      formLoading,
      successMessageActive,
      rejectMessage,
      error,
      actionConfirmationActive,
      rejectionReasons,
      selectedReason,
      selectedSubReasons,
    } = this.state;

    const { cardData } = this.props;
    const { candidate, agent } = cardData;
    const selectedReasonData = rejectionReasons.find(
      (r) => String(r.id) === selectedReason
    );
    const isOtherReason = selectedReasonData?.name?.toLowerCase() === "other";

    return (
      <Wrapper>
        {actionConfirmationActive ? (
          <RejectActionConfirmation
            handleCancel={this.handleCancel}
            handleActionConfirmation={this.handleActionConfirmation}
          />
        ) : (
          <Fragment>
            {formLoading && <Sending />}
            {successMessageActive && (
              <ModalThankYou text="Candidate successfully rejected" />
            )}
            <Form onSubmit={this.handleSubmit}>
              <Label>
                Please let <strong>{agent}</strong> know why you want to reject{" "}
                <strong>{candidate}</strong>
                {isOtherReason
                  ? " (feedback required when Other is selected)."
                  : " (optional message below)."}
                <br />
                <br />
                Before you reject, think of the human being who will receive
                this. <br /> If this were your application, what would you like
                as constructive feedback?
              </Label>

              <Field className="with-padding-top-10">
                <RejectionReasonLabel>
                  Please choose a reason for rejection <Required>*</Required>
                </RejectionReasonLabel>
                <Select
                  value={selectedReason}
                  onChange={this.handleReasonChange}
                >
                  <option value="">Select a Primary Reason</option>
                  {rejectionReasons.map((r) => (
                    <option key={r.id} value={String(r.id)}>
                      {r.name}
                    </option>
                  ))}
                </Select>
              </Field>

              {selectedReason &&
                (() => {
                  const selectedReasonData = rejectionReasons.find(
                    (r) => r.id === selectedReason
                  );
                  const hasSubReasons =
                    selectedReasonData?.subReasons?.length > 0;

                  return (
                    hasSubReasons && (
                      <Field className="margin-top-15">
                        <Label className="margin-bottom-10">
                          Select sub reasons (you can choose multiple)
                        </Label>
                        <CheckBoxesGroup>
                          {selectedReasonData.subReasons.map((sub) => (
                            <CheckBoxesGroupItem key={sub.id}>
                              <CheckBoxLabel>
                                <CheckBoxInput
                                  type="checkbox"
                                  checked={selectedSubReasons.includes(
                                    parseInt(sub.id, 10)
                                  )}
                                  value={sub.id}
                                  onChange={this.handleSubReasonChange}
                                  id={sub.id}
                                />
                                {sub.name}
                                <Indicator />
                              </CheckBoxLabel>
                            </CheckBoxesGroupItem>
                          ))}
                        </CheckBoxesGroup>
                      </Field>
                    )
                  );
                })()}

              <Field className={isOtherReason ? "margin-top-15" : ""}>
                <RejectionReasonLabel>
                  {isOtherReason ? (
                    <>
                      Feedback <Required>*</Required>
                    </>
                  ) : (
                    "Any other feedback (optional)"
                  )}
                </RejectionReasonLabel>
                <TextInput
                  error={error}
                  value={rejectMessage}
                  placeholder={
                    isOtherReason
                      ? "Please specify the reason for rejection"
                      : "Any other feedback..."
                  }
                  onChange={this.handleRejectMessage}
                />
                {error && error.message && (
                  <Error className="animated bounceIn">{error.message}</Error>
                )}
              </Field>

              <ButtonsWrapper>
                <Button
                  secondary
                  buttonName="Cancel"
                  onClickAction={this.handleCancel}
                />
                {!selectedReason ? (
                  <PlaceholderBtn>Reject Candidate</PlaceholderBtn>
                ) : isOtherReason && !rejectMessage.trim() ? (
                  <PlaceholderBtn>Reject Candidate</PlaceholderBtn>
                ) : (
                  <Button primary buttonName="Reject candidate" type="submit" />
                )}
              </ButtonsWrapper>
            </Form>
          </Fragment>
        )}
      </Wrapper>
    );
  }
}

RejectCandidate.propTypes = {
  closeActionModalAfterReset: PropTypes.func.isRequired,
  closeActionModalAfterSubmit: PropTypes.func.isRequired,
  cardData: PropTypes.shape({
    candidate: PropTypes.string,
    agent: PropTypes.string,
    status: PropTypes.string,
    updateStatusLink: PropTypes.string,
  }).isRequired,
  fetchKanbanData: PropTypes.func.isRequired,
};

export default RejectCandidate;
